import React, { useCallback, useEffect, useState } from "react";

/**
 * 데이터 보관 정책 설정 — 본사 관리자 전용.
 *
 * 왜 필요한가:
 *   보관 기간을 바꿀 때마다 개발사에 연락해야 하는 구조는 운영에서 쓸 수 없다.
 *   여기서 저장하면 <다음 정리 주기부터 그 값이 그대로 적용>된다(서버 재시작 없음).
 *
 * 주의:
 *   기간을 줄이면 그만큼 오래된 자료가 다음 정리 때 <실제로 삭제>된다.
 *   되돌릴 수 없으므로 줄일 때는 경고를 보여준다.
 */
const API = process.env.REACT_APP_API_BASE || "";

export default function DataRetentionSettings() {
  const [items, setItems] = useState([]);
  const [keepForever, setKeepForever] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await fetch(`${API}/api/admin/data-retention`, {
        credentials: "include",
        cache: "no-store",
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || "조회 실패");
      setItems(d.items || []);
      setKeepForever(d.keepForever || []);
      const dr = {};
      (d.items || []).forEach((it) => { dr[it.key] = String(it.days); });
      setDraft(dr);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = items.filter((it) => String(it.days) !== String(draft[it.key] ?? it.days));
  // 기간을 <줄이는> 항목만 따로 본다 — 줄이면 실제로 지워지기 때문이다.
  const shrinking = changed.filter((it) => Number(draft[it.key]) < Number(it.days));

  async function save() {
    if (!changed.length) return;
    if (shrinking.length) {
      const lines = shrinking
        .map((it) => `· ${it.label}  ${it.days}일 → ${draft[it.key]}일`)
        .join("\n");
      const ok = window.confirm(
        `보관 기간을 줄이면 그보다 오래된 자료는 다음 정리 때 삭제됩니다.\n삭제된 자료는 되돌릴 수 없습니다.\n\n${lines}\n\n진행하시겠습니까?`
      );
      if (!ok) return;
    }
    setSaving(true); setMsg(""); setErr("");
    try {
      const body = {};
      changed.forEach((it) => { body[it.key] = Number(draft[it.key]); });
      const r = await fetch(`${API}/api/admin/data-retention`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || "저장 실패");
      setItems(d.items || []);
      const dr = {};
      (d.items || []).forEach((it) => { dr[it.key] = String(it.days); });
      setDraft(dr);
      setMsg("저장했습니다. 다음 정리 주기부터 적용됩니다.");
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function runNow() {
    if (!window.confirm("지금 바로 오래된 기록을 정리합니다. 진행하시겠습니까?")) return;
    setSaving(true); setMsg(""); setErr("");
    try {
      const r = await fetch(`${API}/api/admin/data-retention/run`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || "실행 실패");
      const detail = (d.database || [])
        .map((x) => (x.error ? `${x.label}: 실패` : `${x.label} ${x.removed}건`))
        .join(" · ");
      setMsg(`정리 완료 — ${detail || "삭제 대상 없음"}`);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  const box = "rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]";

  return (
    <section className={box}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-bold text-gray-900 dark:text-white">
          데이터 보관 정책
        </h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runNow}
            disabled={saving || loading}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            지금 정리 실행
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || !changed.length}
            className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {saving ? "저장 중…" : changed.length ? `저장 (${changed.length}건)` : "저장"}
          </button>
        </div>
      </div>

      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        설정한 기간이 지난 자료는 자동으로 삭제됩니다. 저장하면 서버 재시작 없이 다음 정리 주기부터 적용됩니다.
      </p>

      {loading ? (
        <div className="py-6 text-sm text-gray-500">불러오는 중…</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const val = draft[it.key] ?? String(it.days);
            const isChanged = String(val) !== String(it.days);
            const isShrink = Number(val) < Number(it.days);
            return (
              <div
                key={it.key}
                className="flex flex-wrap items-center gap-3 rounded-xl bg-gray-50 px-4 py-3 dark:bg-[#16202C]"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {it.label}
                    </span>
                    {it.isDefault && (
                      <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-300">
                        기본값
                      </span>
                    )}
                    {isChanged && (
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                        isShrink
                          ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300"
                          : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                      }`}>
                        {it.days}일 → {val}일
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {it.desc}
                  </div>
                  {it.updatedBy && (
                    <div className="mt-0.5 text-[11px] text-gray-400">
                      마지막 변경 {String(it.updatedAt || "").slice(0, 10)} · {it.updatedBy}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <input
                    type="number"
                    min={it.min}
                    max={it.max}
                    value={val}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, [it.key]: e.target.value }))
                    }
                    className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-right text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">일</span>
                </div>
              </div>
            );
          })}

          {keepForever.map((k) => (
            <div
              key={k.label}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-gray-300 px-4 py-3 dark:border-white/15"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-gray-900 dark:text-white">{k.label}</div>
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{k.desc}</div>
              </div>
              <span className="shrink-0 rounded-lg bg-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:bg-white/10 dark:text-gray-300">
                삭제하지 않음
              </span>
            </div>
          ))}
        </div>
      )}

      {msg && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
          {msg}
        </div>
      )}
      {err && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {err}
        </div>
      )}
    </section>
  );
}
